import React, { useEffect, useRef, useState } from 'lib/react';
import { useSelector } from 'lib/redux';
import {
  isMapPointAddressErrored,
  isMapPointAddressIncomplete,
  isMapPointAddressLoading,
} from 'features/order/Order.utils';
import BuildingDetails from './BuildingDetails.component';

// Stable signature used to detect when an auto-opened form's underlying address has actually
// changed (so we can re-open after a real edit while suppressing reopen on explicit cancel).
const buildAddressKey = (index, address) => {
  if (!address || typeof address !== 'object') return null;
  const { address: line1, city, zip } = address;
  return `${index}|${line1 || ''}|${city || ''}|${zip || ''}`;
};

/**
 * Component that renders list of building details for each map pin.
 *
 * Edit-form expansion: we keep a Set of pin indices that are currently in edit mode rather than a
 * single index because every pin whose geocoded address is missing a required field (e.g. city)
 * needs to be expanded simultaneously — otherwise complex multi-pin orders can have invalid pins
 * the user never realises they need to fix, while the Next button stays disabled forever.
 */
function BuildingDetailsList() {
  const mapPoints = useSelector(({ orderAddresses }) => orderAddresses.mapPoints);
  const [editingIndices, setEditingIndices] = useState(() => new Set());
  // Pins the user explicitly closed via Cancel while the address was still invalid. Keyed on
  // (index + address line + city + zip) so a later edit that changes the address re-arms auto-open.
  const dismissedKeysRef = useRef(new Set());

  useEffect(() => {
    setEditingIndices((prev) => {
      // Drop indices that no longer exist (e.g. user deleted a pin).
      const next = new Set([...prev].filter((i) => i < mapPoints.length));
      mapPoints.forEach((pin, index) => {
        if (!pin?.address || typeof pin.address !== 'object') {
          // Treat malformed shapes like a non-edit state — don't keep a stale form open over them.
          next.delete(index);
          return;
        }
        if (isMapPointAddressLoading(pin) || isMapPointAddressErrored(pin)) {
          // The pin just transitioned to a placeholder (e.g. user dragged it and a new geocode is
          // in flight, or the previous geocode failed). Any currently-open EditBuildingDetails for
          // this pin is now sitting on stale local state — a subsequent Update would overwrite the
          // fresh geocode with outdated fields. Force-close it so the loading / retry row renders.
          next.delete(index);
          return;
        }
        if (!isMapPointAddressIncomplete(pin)) return;
        const key = buildAddressKey(index, pin.address);
        if (key && dismissedKeysRef.current.has(key)) return;
        next.add(index);
      });
      return next;
    });
  }, [mapPoints]);

  // Per-pin wrapper preserving the original child-facing API: pass the pin's own index to open,
  // pass null to close. The optional second arg lets EditBuildingDetails signal an explicit user
  // Cancel so we record a dismissal and don't immediately re-open for the same address.
  const setEditingBuildingIdFor = (currentIndex) => (value, { dismissed = false } = {}) => {
    if (value === null && dismissed) {
      const key = buildAddressKey(currentIndex, mapPoints[currentIndex]?.address);
      if (key) dismissedKeysRef.current.add(key);
    }
    setEditingIndices((prev) => {
      const next = new Set(prev);
      if (value === null) next.delete(currentIndex);
      else if (value === currentIndex) next.add(currentIndex);
      return next;
    });
  };

  return (
    <>
      {mapPoints.map(({ address, building }, index, arr) => (
        <BuildingDetails
          key={`${address.addressline1}-${index + 1}-${arr.length}`}
          isEditing={editingIndices.has(index)}
          setEditingBuildingId={setEditingBuildingIdFor(index)}
          buildingData={address}
          building={building}
          index={index}
        />
      ))}
    </>
  );
}

export default BuildingDetailsList;
